import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

import { Badge, CustomTable, Text } from '../../../shared/uiComponents';
import { GREEN, ORANGE, TEXT } from '../../../shared/Helpers/colors';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { TherapistDetailsProperties } from '../../../redux/API/ClientAPIHelpers/clientProperties';
import { getTherapistsDetails } from '../../../redux/State/clientSlice/userClientSlice';
import { HeaderProperties } from '../../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../../shared/uiComponents/Table/tableHelpers';
import { getBadgeProps } from './utils/getBadgeProps';
import { useScreenWidth } from '../../../shared/Helpers/hooks';
import { UserTie } from '../../../shared/Assets/menuIcons';

const TherapistMobileCard = ({ therapist }: { therapist: TherapistDetailsProperties }) => {
	const assignedDate = therapist.assignedAt;
	const removedDate = therapist.deletedAt;

	const formatDate = (value?: string) => (value ? dayjs(value).format('MM/DD/YYYY') : '-');
	const typeLabel = therapist.authorizationType?.name || '-';
	const roleLabel = therapist.role?.section?.name || therapist.role?.name || '-';

	const { type } = getBadgeProps(therapist.authorizationType, therapist.role.section.name);

	const roleColor = type === 'solidSuccess' ? GREEN.BASE : ORANGE.DARK[16];

	return (
		<div className="providerMobileCard">
			<div className="providerMobileCardHeader">
				<div className="providerMobileCardHeaderIcon">
					<UserTie />
				</div>
				<Text title={therapist.fullName} type="pmd" fontWeight="medium" />
			</div>
			<div className="providerMobileCardDivider" />
			<div className="providerMobileCardBody">
				<div className="providerMobileCardBodyItem">
					<Text title="Type:" type="pxs" color={TEXT.TERTIARY} />
					<Text title={typeLabel} type="psm" fontWeight="medium" color={roleColor} />
				</div>
				<div className="providerMobileCardBodyItem">
					<Text title="Role:" type="pxs" color={TEXT.TERTIARY} />
					<Text title={roleLabel} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
				</div>
				<div className="providerMobileCardBodyItem">
					<Text title="Assigned:" type="pxs" color={TEXT.SECONDARY} />
					<Text title={formatDate(assignedDate)} type="psm" color={TEXT.TERTIARY} />
				</div>
				<div className="providerMobileCardBodyItem">
					<Text title="Removed:" type="pxs" color={TEXT.SECONDARY} />
					<Text title={formatDate(removedDate)} type="psm" color={TEXT.TERTIARY} />
				</div>
			</div>
		</div>
	);
};

const TableBody = ({ therapists }: { therapists: TherapistDetailsProperties[] }) => (
	<tbody>
		{therapists.map((therapist, index) => (
			<CustomTableRow key={index}>
				<RowTableCell>{therapist.fullName}</RowTableCell>
				<RowTableCell>
					{(() => {
						const { label, type } = getBadgeProps(therapist.authorizationType, therapist.role.section.name);

						return <Badge label={label} sizeType="sm" type={type} />;
					})()}
				</RowTableCell>
				<RowTableCell>{therapist.role.section.name}</RowTableCell>
				<RowTableCell>
					{therapist.assignedAt ? (
						dayjs(therapist.assignedAt).format('MM/DD/YYYY')
					) : (
						<span style={{ color: TEXT.QUATERNARY }}>-</span>
					)}
				</RowTableCell>
				<RowTableCell>
					{therapist.deletedAt ? (
						dayjs(therapist.deletedAt).format('MM/DD/YYYY')
					) : (
						<span style={{ color: TEXT.QUATERNARY }}>-</span>
					)}
				</RowTableCell>
			</CustomTableRow>
		))}
	</tbody>
);

const TherapistsList = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const therapistsDetails = useSelector((state) => state.userClient.therapistsDetails);
	const loading = useSelector((state) => state.userClient.loading);

	const screenWidth = useScreenWidth();

	useEffect(() => {
		if (!clientId) return;
		dispatch(getTherapistsDetails({ clientId, includeDeleted: true }));
	}, [clientId, dispatch]);

	if (loading) {
		return (
			<div style={{ width: 'max-content', margin: 'auto' }}>
				<CircularProgress />
			</div>
		);
	}

	const Headers: HeaderProperties[] = [
		{ id: '1', name: 'Full Name' },
		{ id: '2', name: 'Type' },
		{ id: '3', name: 'Role' },
		{ id: '4', name: 'Assigned' },
		{ id: '5', name: 'Removed' },
	];

	return (
		<>
			<div className="therapistsListHeader">
				<div className="listHeaderIcon">
					<UserTie />
				</div>
				<Text type="h6" fontWeight="semiBold" title="Providers" />
			</div>

			{therapistsDetails?.length ? (
				<>
					{screenWidth < 768 ? (
						<div>
							{therapistsDetails.map((therapist) => (
								<TherapistMobileCard key={therapist.id} therapist={therapist} />
							))}
						</div>
					) : (
						<CustomTable
							headers={Headers}
							TableBody={() => <TableBody therapists={therapistsDetails} />}
							data={{ query: therapistsDetails ?? [] }}
							loading={loading}
							hasPagination={false}
						/>
					)}
				</>
			) : null}
		</>
	);
};

export default TherapistsList;
